import { useCallback, useMemo, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { BulkAlarmUpdateError, saveFailure, throwOnBulkFailure, useAlarmsByCameras } from "@src/services/api/hooks";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { api } from "@src/services/api/client";
import { useTenantId } from "@src/features/tenants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AlarmResponse } from "@src/services/api/types";
import { track } from "@visioni/a3s-analytics";
import { AlarmResponseFailed, AlarmResponseSaved, siteLevel } from "@src/features/alarms/analytics";

export type ResponseState = {
  sirens: "on" | "off";
  strobes: "on" | "off";
};

type ResponseOverride = Partial<ResponseState>;
type ResponseField = keyof ResponseState;

const DEFAULT_RESPONSE: ResponseState = { sirens: "off", strobes: "off" };
const RESPONSE_FIELDS: readonly ResponseField[] = ["sirens", "strobes"];

export function useManageAlarmResponse() {
  const router = useRouter();
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const { siteId } = useLocalSearchParams<{ siteId: string }>();
  const { siteCameras, cameraKeys } = useSiteCameraKeys(siteId);

  const alarmQueries = useAlarmsByCameras(siteId, cameraKeys);

  const isLoading = alarmQueries.some((q) => q.isLoading);

  const [overrides, setOverrides] = useState<Map<string, ResponseOverride>>(new Map());

  const initialResponses = useMemo(() => {
    const map = new Map<string, ResponseState>();
    for (let i = 0; i < cameraKeys.length; i++) {
      const config = alarmQueries[i]?.data;
      const response = config?.settings?.response;
      map.set(cameraKeys[i], {
        sirens: response?.sirens ?? "off",
        strobes: response?.strobes ?? "off",
      });
    }
    return map;
  }, [cameraKeys, alarmQueries]);

  const effectiveResponses = useMemo(() => {
    const map = new Map(initialResponses);
    for (const [key, override] of overrides) {
      const initial = map.get(key) ?? DEFAULT_RESPONSE;
      map.set(key, { ...initial, ...override });
    }
    return map;
  }, [initialResponses, overrides]);

  const toggleResponse = useCallback(
    (cameraKey: string, field: ResponseField) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        const currentOverride = next.get(cameraKey) ?? {};
        const effectiveCurrent = currentOverride[field] ?? initialResponses.get(cameraKey)?.[field] ?? "off";
        next.set(cameraKey, {
          ...currentOverride,
          [field]: effectiveCurrent === "on" ? "off" : "on",
        });
        return next;
      });
    },
    [initialResponses],
  );

  const allSirensOn = useMemo(
    () => cameraKeys.length > 0 && cameraKeys.every((k) => (effectiveResponses.get(k)?.sirens ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const allStrobesOn = useMemo(
    () => cameraKeys.length > 0 && cameraKeys.every((k) => (effectiveResponses.get(k)?.strobes ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const toggleAllSirens = useCallback(() => {
    const newValue = allSirensOn ? "off" : "on";
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const key of cameraKeys) {
        next.set(key, { ...(next.get(key) ?? {}), sirens: newValue });
      }
      return next;
    });
  }, [allSirensOn, cameraKeys]);

  const toggleAllStrobes = useCallback(() => {
    const newValue = allStrobesOn ? "off" : "on";
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const key of cameraKeys) {
        next.set(key, { ...(next.get(key) ?? {}), strobes: newValue });
      }
      return next;
    });
  }, [allStrobesOn, cameraKeys]);

  const hasChanges = useMemo(() => {
    for (const [key, override] of overrides) {
      const initial = initialResponses.get(key) ?? DEFAULT_RESPONSE;
      for (const field of RESPONSE_FIELDS) {
        const overrideValue = override[field];
        if (overrideValue !== undefined && overrideValue !== initial[field]) return true;
      }
    }
    return false;
  }, [overrides, initialResponses]);

  const updateResponses = useMutation({
    /** Resolves with the number of cameras updated, or `null` when there was no site to save. */
    mutationFn: async (): Promise<number | null> => {
      if (!siteId) return null;

      const camerasToUpdate: { cameraKey: string; response: AlarmResponse }[] = [];

      for (const [cameraKey, override] of overrides) {
        const initial = initialResponses.get(cameraKey);
        if (!initial) continue;

        const effective: ResponseState = { ...initial, ...override };
        if (effective.sirens === initial.sirens && effective.strobes === initial.strobes) continue;

        const alarmIndex = cameraKeys.indexOf(cameraKey);
        const config = alarmQueries[alarmIndex]?.data;
        const currentResponse = config?.settings?.response;

        camerasToUpdate.push({
          cameraKey,
          response: {
            recording: currentResponse?.recording ?? "on",
            monitoring: "on",
            sirens: effective.sirens,
            strobes: effective.strobes,
          },
        });
      }

      const results = await Promise.allSettled(
        camerasToUpdate.map(async ({ cameraKey, response }) => {
          const result = await api.alarms.updateResponse(tenantId, siteId!, cameraKey, response);
          queryClient.setQueryData(["alarms", tenantId, siteId, cameraKey], result);
          return result;
        }),
      );

      throwOnBulkFailure(results, (failedCount) => `Failed to update ${failedCount} cameras`);
      return camerasToUpdate.length;
    },
    onSuccess: (cameraCount) => {
      if (cameraCount !== null) {
        track(AlarmResponseSaved, {
          ...siteLevel(tenantId, siteId, cameraCount),
          sirens: undefined,
          strobes: undefined,
        });
      }
      showSuccessToast({ message: "Alarm response updated" });
      router.back();
    },
    onError: (error) => {
      // `camera_count` is the cameras attempted; unknown if the save failed before reaching them.
      track(AlarmResponseFailed, {
        ...siteLevel(tenantId, siteId, error instanceof BulkAlarmUpdateError ? error.attemptedCount : undefined),
        sirens: undefined,
        strobes: undefined,
        ...saveFailure(error),
      });
      showErrorToast({ message: "Error", description: "Failed to update alarm response." });
      console.error("Failed to update alarm response:", error);
    },
  });

  const isSaving = updateResponses.isPending;

  const save = useCallback(() => updateResponses.mutate(), [updateResponses]);

  return {
    siteCameras,
    cameraKeys,
    siteId,
    tenantId,
    isLoading,
    effectiveResponses,
    toggleResponse,
    allSirensOn,
    allStrobesOn,
    toggleAllSirens,
    toggleAllStrobes,
    hasChanges,
    isSaving,
    save,
  };
}
